// MigrationCrunch — per-source-CRM "how to get your data out" guides.
// Keys match SOURCE_CRMS ids in MigrationCrunchPage.js (and pairConfig.sourceId).
// image: path under /public. null = written steps only.

export const EXPORT_GUIDES = {
  salesforce: {
    name: 'Salesforce',
    image: '/export-guides/salesforce.jpg',
    fileYouGet: 'A ZIP of CSVs — the one we want is Contact.csv (plus Account.csv / Note.csv if present)',
    steps: [
      'Log in, then click the gear icon (top right) → Setup.',
      'In the Quick Find box on the left, type "Data Export" and click Data Export.',
      'Click "Export Now". Check the boxes for Contacts, Accounts, and Notes.',
      'Salesforce emails you a download link (usually minutes; up to 48h on huge orgs). Download the ZIP.',
      'Unzip it and upload Contact.csv here.',
    ],
    tip: 'Small org shortcut: Reports → New Report → "Contacts & Accounts" → run it → Export as CSV. Instant, no waiting.',
  },
  hubspot: {
    name: 'HubSpot',
    image: '/export-guides/hubspot.jpg',
    fileYouGet: 'A CSV, delivered as an emailed download link',
    steps: [
      'Log in → top menu CRM → Contacts.',
      'Above the contact table, click "Export" (on some plans it hides under the ⋯ More menu).',
      'Pick file format CSV and "All properties", then confirm.',
      'HubSpot emails you a download link — click it and save the CSV.',
    ],
    tip: 'Companies and Deals export the exact same way from CRM → Companies and CRM → Deals.',
  },
  pipedrive: {
    name: 'Pipedrive',
    image: '/export-guides/pipedrive.jpg',
    fileYouGet: 'A CSV (or XLSX) per list — People, Organizations, Deals',
    steps: [
      'Log in → left sidebar Contacts → People.',
      'Click the "..." (three dots) at the top right of the list → "Export filter results".',
      'Choose CSV and export.',
      'Repeat for Contacts → Organizations (and Deals) if you want those too.',
    ],
    tip: 'Want everything at once? Profile icon → Company settings → "Export data" gives a full account backup.',
  },
  zoho: {
    name: 'Zoho CRM',
    image: '/export-guides/zoho.jpg',
    fileYouGet: 'A ZIP that unzips to a CSV per module',
    steps: [
      'Log in → click the gear icon (Setup, top right).',
      'Under "Data Administration", click "Export".',
      'Start an export and pick the module "Contacts". Repeat for Accounts / Deals / Notes.',
      'When it finishes, download the ZIP from the same page and unzip it.',
    ],
    tip: 'Zoho caps exports at 200,000 records each — big databases just take a couple of batches.',
  },
  act: {
    name: 'ACT! (classic)',
    image: '/export-guides/act.jpg',
    fileYouGet: 'A text/CSV export — or skip the wizard and upload your raw .dbf file directly',
    steps: [
      'Open ACT! → File menu → Data Exchange → Export…',
      'In the Export Wizard, pick file type "Text-Delimited" and name the file contacts.csv.',
      'Choose "Contact records" → "All records", keep the field list as-is, click Finish.',
      'Or the zero-effort route: find your database\'s .dbf file and upload it here directly — we parse ACT! natively.',
    ],
    tip: 'Your notes usually live in a separate history file. Upload it too — our AI matches every note back to the right contact.',
  },
  'act-365': {
    name: 'ACT! 365 / Premium Cloud',
    image: '/export-guides/act365.jpg',
    fileYouGet: 'An .xlsx spreadsheet — upload it here as-is',
    steps: [
      'Log in to ACT! and open the Contacts list view.',
      'Tick the select-all checkbox at the top left of the list.',
      'Click the "Export to Excel" button (spreadsheet icon) in the toolbar.',
      'The .xlsx downloads straight to your computer — that\'s the file we want.',
    ],
    tip: 'Custom user fields come along in the export — our AI maps them even when ACT! has renamed them.',
  },
  goldmine: {
    name: 'GoldMine',
    image: '/export-guides/goldmine.jpg',
    fileYouGet: 'A CSV (or DBF) of contact records',
    steps: [
      'Open GoldMine → Tools menu → Import/Export Wizard → "Export Contact Records".',
      'Pick "ASCII CSV" as the format.',
      'Select all fields (include notes/history if offered) and click Finish.',
    ],
    tip: 'Include the Key1–Key5 fields — they usually carry your custom categories, and our AI knows what to do with them.',
  },
  outlook: {
    name: 'Outlook',
    image: '/export-guides/outlook.jpg',
    fileYouGet: 'A CSV — or just upload your whole .pst file and skip the export entirely',
    steps: [
      'Open Outlook (desktop) → File → Open & Export → Import/Export.',
      'Choose "Export to a file" → Next → "Comma Separated Values".',
      'Select your Contacts folder → Next → pick where to save the CSV.',
      'Or easier: upload your .pst file here directly — we parse it natively, no Outlook needed.',
    ],
    tip: 'Outlook Categories become tags on the other side — they survive the trip.',
  },
  excel: {
    name: 'Excel / Google Sheets',
    image: '/export-guides/excel.jpg',
    fileYouGet: 'A CSV or the .xlsx itself — we read both',
    steps: [
      'Make sure row 1 holds your column names (First Name, Email, Phone…). Messy names are fine — the AI figures them out.',
      'Excel: File → Save As → "CSV (Comma delimited)".',
      'Google Sheets: File → Download → "Comma Separated Values (.csv)".',
      'Or skip the conversion and upload the .xlsx directly.',
    ],
    tip: 'Columns in Spanish, French, or 3am-assistant-speak? Upload anyway. Field mapping is exactly what our AI is for.',
  },
  maximizer: {
    name: 'Maximizer',
    image: '/export-guides/maximizer.jpg',
    fileYouGet: 'A CSV/XLSX of your Address Book',
    steps: [
      'Open Maximizer → File menu → "Import/Export/Transfer".',
      'Choose "Export to Excel / CSV" for the Address Book.',
      'Tick "include user-defined fields and notes" if prompted, then Export.',
    ],
    tip: 'UDF-prefixed columns are normal — our AI recognizes Maximizer\'s field structure.',
  },
  sage: {
    name: 'Sage CRM',
    image: null,
    fileYouGet: 'A CSV per list — Persons, Companies, Communications',
    steps: [
      'Log in to Sage CRM and open your Persons list (or run a Persons report).',
      'Use the "Export to CSV" / Excel button on the list toolbar.',
      'Repeat for Companies and Communications (that\'s where your notes live).',
    ],
    tip: 'Sage exports are famously messy — that\'s fine. Cleaning them is literally our job.',
  },
  generic: {
    name: 'your current CRM',
    image: null,
    fileYouGet: 'Any CSV or Excel file of your contacts',
    steps: [
      'Log in to your CRM and open the contact list.',
      'Look for an "Export", "Download", or spreadsheet icon — usually top-right of the list or under Settings → Data.',
      'Choose CSV (or Excel) and download it.',
      'That\'s all we need. Notes in a separate file? Great — we match them back automatically.',
    ],
    tip: 'Can\'t find the export button? Pick your CRM in the dropdown above for exact click-by-click steps.',
  },
};
